import type { SessionStatus, SessionSummary } from '@diana/contracts';

export type ClientActivityGroup = 'requests' | 'progress' | 'past' | 'other';
export type ClientActivity = {
  group: ClientActivityGroup;
  stage: SessionStatus | null;
  count: number;
  giftPurchases?: number;
  date: string | null;
  dateLabel: 'Next scheduled' | 'Latest scheduled' | 'Date not set';
};

export function clientActivity(
  clientId: string,
  shoots: readonly SessionSummary[],
  now = Date.now(),
): ClientActivity {
  const linked = shoots.filter((shoot) => shoot.clients.some((client) => client.id === clientId));
  const stage =
    (['NewRequest', 'InDiscussion', 'Scheduled', 'Editing'] as const).find((status) =>
      linked.some((shoot) => shoot.status === status),
    ) ?? null;
  const group =
    stage === 'NewRequest' ? 'requests' : stage ? 'progress' : linked.length ? 'past' : 'other';
  let next: string | null = null;
  let latest: string | null = null;
  for (const shoot of linked) {
    const date = shoot.scheduledStartAt;
    if (!date) continue;
    const timestamp = Date.parse(date);
    if (latest === null || timestamp > Date.parse(latest)) latest = date;
    if (
      shoot.status === 'Scheduled' &&
      timestamp > now &&
      (next === null || timestamp < Date.parse(next))
    )
      next = date;
  }
  return {
    group,
    stage,
    count: linked.filter(
      (shoot) =>
        !shoot.isGiftCard || !shoot.giftContacts || shoot.giftContacts.recipientId === clientId,
    ).length,
    ...(linked.some((shoot) => shoot.isGiftCard && shoot.giftContacts?.buyerId === clientId)
      ? {
          giftPurchases: linked.filter(
            (shoot) => shoot.isGiftCard && shoot.giftContacts?.buyerId === clientId,
          ).length,
        }
      : {}),
    date: next ?? latest,
    dateLabel: next ? 'Next scheduled' : latest ? 'Latest scheduled' : 'Date not set',
  };
}

export function clientInitials(name: string) {
  const words = name
    .trim()
    .split(/\s+/)
    .map((word) => word.match(/\p{L}/u)?.[0])
    .filter((letter): letter is string => Boolean(letter));
  if (!words.length) return '?';
  return (
    words[0]!.toLocaleUpperCase() + (words.length > 1 ? words.at(-1)!.toLocaleUpperCase() : '')
  );
}
